/**
 * <sticky-header>
 *
 * State 1 — at the top of the page: transparent, offset down, rounded corners.
 * State 2 — scrolled: any scroll down slides the header out of view; scrolling
 *           up brings it back pinned, with a solid background.
 *
 * data-hide-offset defines how far the page can scroll before hiding kicks in.
 * 0 (default) = the header hides on the very first scroll down.
 *
 * data-always-pinned keeps the scrolled design (state 2) permanently — used on
 * every page except the homepage. Hide/reveal behaviour is unaffected. While
 * such a page is at the top, ch--at-top moves the bar to its natural flow
 * position (below the announcement bar that precedes it in the header group)
 * instead of covering it. That position is measured into --ch-flow-offset.
 *
 * Dropdown (mega menu): [data-mega] expands the header downwards. Opening
 * copies the state-2 design (background, colors, shadow) onto the bar without
 * moving it — the position, insets and radii of the current state stay as
 * they are. While open, the header is kept visible; any real scroll of the
 * page closes the menu.
 *
 * How it opens depends on the screen and the input device:
 *   Desktop, mouse      hovering the nav link marked data-mega-trigger opens
 *                       it, leaving the header closes it ("hover" mode).
 *   Desktop, keyboard   focusing the trigger opens it, Escape or tabbing out
 *                       of the header closes it.
 *   Desktop, touch      there is no hover, so the first tap on the trigger
 *                       opens the menu instead of following the link. A
 *                       second tap follows the link (or closes the menu if
 *                       the button has no link). ("click" mode)
 *   Tablet and mobile   the nav links are hidden below 990px; the menu button
 *                       [data-menu-toggle] opens and closes the same panel.
 *                       ("click" mode)
 * In "click" mode the menu stays open until the button is used again, Escape
 * is pressed, something outside the header is tapped, or the page scrolls —
 * a mouse leaving the header does not close it.
 *
 * Tab activation is sticky — the last hovered or tapped tab stays active
 * until a different tab is chosen. Tab switching is a carousel: the panels
 * form a track in tab order. The JS only marks the active panel (is-active)
 * and the ones before it (is-before); the CSS parks inactive panels off to
 * the left/right and transitions between those positions, so the slide
 * direction follows the tab order. If the two panels differ in height, the
 * track height is animated as well.
 *
 * Public API:
 *   header.lock()   → keep the header visible (e.g. while a drawer is open)
 *   header.unlock() → hand control back to the scroll listener
 */
if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onOutsidePointerDown = this.onOutsidePointerDown.bind(this);
      this.ticking = false;
      this.locked = false;
      this.megaOpen = false;
      this.megaMode = null;
      this.megaOpener = null;
      this.megaPrimed = false;
      this.flowOffset = 0;
    }

    connectedCallback() {
      this.delta = parseInt(this.dataset.delta || '6', 10);
      this.hideOffset = parseInt(this.dataset.hideOffset || '0', 10);
      this.alwaysPinned = this.hasAttribute('data-always-pinned');
      this.lastScrollY = Math.max(0, window.scrollY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.innerEl = this.querySelector('.ch__inner') || this;
      this.megaEl = this.querySelector('[data-mega]');
      // The in-flow section wrapper around this (fixed) element. Its position
      // is where the bar sits on subpages while the page is at the top.
      this.wrapperEl = this.closest('.shopify-section') || this.parentElement;
      this.measure();
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.innerEl);
        this.observeFlowNeighbour();
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }
      // Fonts/images above the header can change its flow position after
      // first paint.
      window.addEventListener('load', this.onResize, { once: true });
      if (window.Shopify && Shopify.designMode) {
        ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) => {
          document.addEventListener(type, () => {
            this.observeFlowNeighbour();
            this.measure();
          });
        });
      }
      window.addEventListener('scroll', this.onScroll, { passive: true });
      // Keyboard users tabbing into a hidden header get it back.
      this.addEventListener('focusin', () => this.reveal());
      this.setupMega();
      this.update(window.scrollY, true);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('pointerdown', this.onOutsidePointerDown);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get disabled() {
      // Reduced motion: never slide out — behave like "hide on scroll" is off.
      return this.hasAttribute('data-disabled') || this.reducedMotion.matches;
    }

    measure() {
      // Exclude the dropdown from the measurement: --custom-header-height must
      // stay the height of the bar itself, otherwise the spacer grows while
      // the menu is open and the page below jumps.
      const megaHeight = this.megaEl ? this.megaEl.offsetHeight : 0;
      this.headerHeight = this.innerEl.offsetHeight - megaHeight;
      document.documentElement.style.setProperty('--custom-header-height', `${this.headerHeight}px`);
      this.measureFlowOffset();
    }

    // Document-y of the header's in-flow wrapper — i.e. directly below whatever
    // precedes the header in the header group (announcement bar).
    measureFlowOffset() {
      if (!this.wrapperEl) return;
      const top = this.wrapperEl.getBoundingClientRect().top + window.scrollY;
      this.flowOffset = Math.max(0, Math.round(top));
      document.documentElement.style.setProperty('--ch-flow-offset', `${this.flowOffset}px`);
    }

    // Re-measure when the section above the header (announcement bar) resizes.
    observeFlowNeighbour() {
      if (!this.resizeObserver || !this.wrapperEl) return;
      if (this.flowNeighbour) this.resizeObserver.unobserve(this.flowNeighbour);
      this.flowNeighbour = this.wrapperEl.previousElementSibling;
      if (this.flowNeighbour) this.resizeObserver.observe(this.flowNeighbour);
    }

    onResize() {
      this.measure();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update(window.scrollY);
        this.ticking = false;
      });
    }

    update(rawScrollY, force = false) {
      // Clamp: iOS rubber-band overscroll reports negative values.
      const scrollY = Math.max(0, rawScrollY);

      // Subpages: while at the top the bar sits below the announcement bar.
      this.classList.toggle('ch--at-top', scrollY <= this.hideOffset);

      // Any real scroll of the page closes the dropdown. Scrolling *inside*
      // the open menu (tablet/mobile, when it is taller than the screen) does
      // not move the page and therefore keeps it open.
      if (this.megaOpen && Math.abs(scrollY - this.lastScrollY) > this.delta) {
        this.closeMega();
      }

      // Kept visible: by lock() (a drawer) or by the open dropdown. The two
      // are tracked separately, so closing the menu never releases a lock
      // that a drawer asked for.
      if (this.locked || this.megaOpen) {
        this.reveal();
        this.lastScrollY = scrollY;
        return;
      }

      // At (or above) the point where hiding starts.
      // hideOffset defaults to 0, so the bar leaves state 1 on any scroll.
      if (scrollY <= this.hideOffset) {
        if (this.alwaysPinned) {
          // Subpages: the scrolled design stays, the bar just never hides here.
          this.classList.add('ch--pinned');
          this.classList.remove('ch--hidden');
        } else {
          // Homepage: back to the transparent state 1.
          this.classList.remove('ch--pinned', 'ch--hidden');
        }
        this.lastScrollY = scrollY;
        return;
      }

      // Hide-on-scroll disabled, or the page loaded already scrolled:
      // show state 2 without animating out first.
      if (this.disabled || force) {
        this.classList.add('ch--pinned');
        this.classList.remove('ch--hidden');
        this.lastScrollY = scrollY;
        return;
      }

      const diff = scrollY - this.lastScrollY;
      if (Math.abs(diff) <= this.delta) return;

      if (diff > 0) {
        // Scrolling down — slide away. Deliberately no ch--pinned here:
        // hiding straight out of state 1 must not flash the solid background.
        this.classList.add('ch--hidden');
      } else {
        // Scrolling up — reveal in state 2.
        this.classList.add('ch--pinned');
        this.classList.remove('ch--hidden');
      }
      this.lastScrollY = scrollY;
    }

    reveal() {
      this.classList.remove('ch--hidden');
      // When revealed mid-page (focus, lock), show the solid state so the
      // transparent bar never floats over arbitrary content.
      if (Math.max(0, window.scrollY) > this.hideOffset) {
        this.classList.add('ch--pinned');
      }
    }

    lock() {
      this.locked = true;
      this.reveal();
    }

    unlock() {
      this.locked = false;
      this.lastScrollY = Math.max(0, window.scrollY);
    }

    /* ----------------------------------------------------------------------
       Dropdown (mega menu)
       ---------------------------------------------------------------------- */

    setupMega() {
      if (!this.megaEl) return;
      // Desktop opener: the nav link with the dropdown. Hidden below 990px.
      this.megaTrigger = this.querySelector('[data-mega-trigger]');
      // Tablet/mobile opener: the menu button. Hidden from 990px.
      this.menuToggle = this.querySelector('[data-menu-toggle]');
      if (!this.megaTrigger && !this.menuToggle) return;

      // On tablet/mobile the content scrolls inside the menu when it is taller
      // than the screen; every open starts at the top again.
      this.megaScroller = this.megaEl.querySelector('[data-mega-scroll]');

      const tabs = Array.from(this.megaEl.querySelectorAll('[data-mega-tab]'));
      const panels = Array.from(this.megaEl.querySelectorAll('[data-mega-panel]'));
      const track = this.megaEl.querySelector('[data-mega-panels]');
      const initial = panels.find((p) => p.classList.contains('is-active'));
      this.activeMegaTab = initial ? initial.dataset.megaPanel : '0';

      // The track only carries an inline height while it animates between two
      // panels of different height; afterwards it goes back to auto so it keeps
      // following the active panel (images are sized in vw).
      if (track) {
        const releaseHeight = (event) => {
          if (event.target === track && event.propertyName === 'height') track.style.height = '';
        };
        track.addEventListener('transitionend', releaseHeight);
        track.addEventListener('transitioncancel', releaseHeight);
      }

      // Sticky activation: the last hovered tab stays active until another
      // tab is hovered — leaving a tab changes nothing.
      this.activateMegaTab = (index) => {
        if (index === this.activeMegaTab) return;
        const target = Number(index);
        const next = panels.find((p) => p.dataset.megaPanel === index);
        if (!next) return;
        this.activeMegaTab = index;

        // Read the current height first — mid-animation this is the in-between
        // value, so a quick second switch continues from where the track is.
        const from = track ? track.getBoundingClientRect().height : 0;

        tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.megaTab === index));
        // is-before = parked on the left, neither class = parked on the right.
        // The CSS transitions between those positions, which gives the slide
        // its direction: later tab → products move left, earlier tab → right.
        panels.forEach((p) => {
          const i = Number(p.dataset.megaPanel);
          p.classList.toggle('is-active', i === target);
          p.classList.toggle('is-before', i < target);
        });

        if (!track) return;
        const to = next.offsetHeight;
        if (!this.megaOpen || this.reducedMotion.matches || Math.abs(from - to) < 1) {
          track.style.height = '';
          return;
        }
        track.style.height = `${from}px`;
        void track.offsetHeight; // commit the start value before transitioning
        track.style.height = `${to}px`;
      };

      // mouseenter covers the mouse; a tap arrives as click (touch screens
      // have no hover), keyboard as focus.
      tabs.forEach((tab) => {
        const index = tab.dataset.megaTab;
        tab.addEventListener('mouseenter', () => this.activateMegaTab(index));
        tab.addEventListener('focus', () => this.activateMegaTab(index));
        tab.addEventListener('click', () => this.activateMegaTab(index));
      });

      /* -- Desktop opener: the nav link ----------------------------------- */

      if (this.megaTrigger) {
        const trigger = this.megaTrigger;
        // A button without a link renders href="#". Following it would only
        // jump to the top of the page (and thereby close the menu again).
        const hasLink = () => {
          const href = trigger.getAttribute('href');
          return Boolean(href) && href !== '#';
        };

        // Pointer events instead of mouse events: a tap also fires emulated
        // mouseenter/mouseleave, which would open and immediately re-close
        // the menu. pointerType tells the real mouse from the finger.
        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.openMega('hover', trigger);
        });
        trigger.addEventListener('focus', () => {
          if (!this.restoringFocus) this.openMega('hover', trigger);
        });

        // Touch and pen: remember on pointerdown whether the menu was already
        // open. Some browsers focus the link before the click arrives, which
        // opens the menu through the focus listener above — without this
        // snapshot the click could not tell a first tap from a second one.
        trigger.addEventListener('pointerdown', (event) => {
          this.triggerTap = { touch: event.pointerType !== 'mouse', wasOpen: this.megaOpen };
        });
        trigger.addEventListener('click', (event) => {
          const tap = this.triggerTap;
          this.triggerTap = null;
          if (tap && tap.touch) {
            if (!tap.wasOpen) {
              // First tap: open instead of navigating.
              event.preventDefault();
              this.openMega('click', trigger);
            } else if (!hasLink()) {
              // Second tap, no link: behave like a toggle.
              event.preventDefault();
              this.closeMega();
            }
            // Second tap with a link: let the browser follow it.
            return;
          }
          // Mouse or keyboard: the menu is already open (hover/focus).
          if (!hasLink()) event.preventDefault();
        });
      }

      /* -- Tablet/mobile opener: the menu button -------------------------- */

      if (this.menuToggle) {
        this.menuToggle.addEventListener('click', () => {
          if (this.megaOpen) {
            this.closeMega();
          } else {
            this.openMega('click', this.menuToggle);
          }
        });
      }

      /* -- Closing --------------------------------------------------------- */

      // Mouse leaves the header: only closes a menu that was opened by
      // hovering. A menu opened by tap/click stays until it is dismissed.
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && this.megaMode === 'hover') this.closeMega();
      });

      // Tap or click anywhere outside the header.
      document.addEventListener('pointerdown', this.onOutsidePointerDown);

      // Keyboard focus moves on to something outside the header. A click on
      // empty space inside the menu also blurs the focused element, but then
      // relatedTarget is null — that must not close the menu.
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeMega();
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.megaOpen) return;
        const opener = this.megaOpener;
        this.closeMega();
        // Hand focus back to whatever opened the menu — without letting the
        // trigger's focus listener open it again.
        if (opener && opener.getClientRects().length) {
          this.restoringFocus = true;
          opener.focus();
          this.restoringFocus = false;
        }
      });

      // The cart and search buttons open drawers that cover the page — the
      // menu must not stay open underneath them.
      this.addEventListener('click', (event) => {
        if (this.megaOpen && event.target.closest('[data-mcd-toggle], [data-msd-toggle]')) this.closeMega();
      });

      // Crossing the 990px breakpoint (rotating a tablet, resizing the
      // window) swaps the opener and the layout — start from a closed menu.
      const desktop = window.matchMedia('(min-width: 990px)');
      const onBreakpoint = () => this.closeMega();
      if (desktop.addEventListener) {
        desktop.addEventListener('change', onBreakpoint);
      } else if (desktop.addListener) {
        desktop.addListener(onBreakpoint); // Safari < 14
      }

      // Theme editor: selecting a "Dropdown tab" block opens the menu on that
      // tab so it can be styled without hovering — at every preview size.
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (this.contains(event.target) && event.target.matches('[data-mega-panel]')) {
            this.openMega('click');
            this.activateMegaTab(event.target.dataset.megaPanel);
          }
        });
        document.addEventListener('shopify:block:deselect', (event) => {
          if (this.contains(event.target) && event.target.matches('[data-mega-panel]')) {
            this.closeMega();
          }
        });
      }
    }

    onOutsidePointerDown(event) {
      if (this.megaOpen && !this.contains(event.target)) this.closeMega();
    }

    // Inactive panels are parked off-screen, where lazy images may not load
    // until they slide in — and then pop in mid-animation. The first open
    // switches all dropdown images to eager, so every tab is ready. Nothing is
    // fetched for visitors who never open the menu.
    primeMegaImages() {
      if (this.megaPrimed || !this.megaEl) return;
      this.megaPrimed = true;
      this.megaEl.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    // Both openers announce the state; only one of them is visible at a time.
    setMegaExpanded(expanded) {
      [this.megaTrigger, this.menuToggle].forEach((el) => {
        if (el) el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    /**
     * mode   'hover' — closes when the mouse leaves the header
     *        'click' — stays open until it is dismissed (tap, menu button)
     * opener the element that gets focus back on Escape
     */
    openMega(mode = 'hover', opener = null) {
      if (!this.megaEl) return;
      if (this.megaOpen) {
        // A tap on an already hover/focus-opened menu makes it stay.
        if (mode === 'click') this.megaMode = 'click';
        return;
      }
      this.megaOpen = true;
      this.megaMode = mode;
      this.megaOpener = opener || this.megaTrigger || this.menuToggle;
      this.primeMegaImages();
      if (this.megaScroller) this.megaScroller.scrollTop = 0;
      // Only ch--mega-open: the CSS copies the state-2 *design* (background,
      // colors, shadow) onto the bar, but position, insets and corner radii of
      // the current state are untouched, so no element moves on hover.
      this.classList.add('ch--mega-open');
      this.setMegaExpanded(true);
      // update() keeps the header visible for as long as megaOpen is set.
      this.reveal();
    }

    closeMega() {
      if (!this.megaOpen) return;
      this.megaOpen = false;
      this.megaMode = null;
      this.classList.remove('ch--mega-open');
      this.setMegaExpanded(false);
      // Scroll tracking restarts from here, like after unlock().
      this.lastScrollY = Math.max(0, window.scrollY);
    }
  }
  customElements.define('sticky-header', StickyHeader);
}
